import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    fetchPostPaymentMethod,
    type PostPaymentMethodRequestBody
} from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';

import {
    type BankDetailsFormValues,
    createBankDetailsSchema
} from './bankDetailsSchema';
import { normalizeSortCode } from '../../../../../../helpers/normalizers';
import * as IwDataDogLogger from '../../../../../../iwLogger/IwDataDogLogger';
import { useStateKey } from '../../../../../../iwQueryClient/useStateKey';
import { LinkUKBankAccount } from '../../../../../modals/addPaymentMethodModal/linkBankAccount/LinkUKBankAccount';
import { InnerLayout } from '../../InnerLayout/InnerLayout';
import { useRequirementsProgress } from '../../useRequirementsProgress';
import { useGetBankDetails } from '../useGetBankDetails';

export const BankDetailsForm = () => {
    const history = useHistory();
    const { search } = useLocation();
    const intl = useIntl();
    const stateKey = useStateKey();
    const queryClient = useQueryClient();
    const { bankDetails: existingBankDetails } = useGetBankDetails();
    const progress = useRequirementsProgress();

    const { mutateAsync, isPending } = useMutation({
        mutationFn: async (formValues: BankDetailsFormValues) => {
            const body: PostPaymentMethodRequestBody = {
                data: {
                    bank_details: {
                        account_number: formValues.accountNumber,
                        sort_code: formValues.sortCode
                    },
                    roles: { funding: 'enabled', repayment: null }
                }
            };

            await fetchPostPaymentMethod({ stateKey, body });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['payment_method', stateKey]
            });
            queryClient.invalidateQueries({
                queryKey: ['fetchGetFundingRequirementsCreditFacility']
            });
        },
        onError: (error: Error) => {
            IwDataDogLogger.logError(
                'Failed to submit bank details for funding',
                { tags: { reporter: 'BankDetailsForm' } },
                error
            );
        }
    });

    const methods = useForm<BankDetailsFormValues>({
        resolver: yupResolver(createBankDetailsSchema(intl)),
        defaultValues: { accountNumber: '', sortCode: '' }
    });

    const { reset } = methods;

    useEffect(() => {
        if (existingBankDetails) {
            reset({
                accountNumber: existingBankDetails.accountNumber,
                sortCode: normalizeSortCode(existingBankDetails.sortCode)
            });
        }
    }, [existingBankDetails, reset]);

    const onSubmit = async (values: BankDetailsFormValues) => {
        await mutateAsync(values);
        tracking.ampli.fundingRequirementsBankDetailsCompleted({
            version: '2.0'
        });
        tracking.ampli.fundingRequirementCompleted({
            requirement: 'bank_details',
            progress: { ...progress, bank_details: true },
            version: '2.0'
        });
        history.push(
            maintainStateKey({ pathname: '/funding-requirements/', search })
        );
    };

    return (
        <InnerLayout
            requirementType="BANK_DETAILS"
            title={
                <FormattedMessage
                    id="_.funding_requirements.bank_details_form.title"
                    defaultMessage="Add bank details to receive funds"
                />
            }
            subheading={
                <FormattedMessage
                    id="_.funding_requirements.bank_details_form.subheading"
                    defaultMessage="This is where we'll send your funds. This must be your main business account, a personal account will not be funded."
                />
            }
        >
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                    <LinkUKBankAccount
                        accountNumberPlaceholder="00000000"
                        sortCodePlaceholder="00-00-00"
                    />
                    <Button
                        variant="primary-rounded"
                        type="submit"
                        loading={isPending}
                        className="mt-xl"
                    >
                        <FormattedMessage
                            id="_.funding_requirements.bank_details_form.submit"
                            defaultMessage="Confirm and return to list"
                        />
                    </Button>
                </form>
            </FormProvider>
        </InnerLayout>
    );
};
